import React, { useState, useEffect, FC } from 'react'
import DOMPurify from 'dompurify'
import { getWidget, getWidgetSettings, getWidgetContent } from '@src/queries/widgets/api'
import { style } from 'typestyle'
import { ActionButton } from '@src/components/ActionButton'
import { TextArea } from '@src/components/TextArea'
import { FormLine } from '@src/components/Form/FormLine'
import { PerfectScrollbar } from '@src/components/PerfectScrollbar'
import { DropDown } from '@src/components/DropDown'
import { useQueryProductsAvailableCountries } from '@src/queries/products/useQueryProducts'
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig'
import { SquareIcon } from '@src/pages/Widget/Edit/Icons/ThumbnailCornersIcons/Square'
import { RoundedIcon } from '@src/pages/Widget/Edit/Icons/ThumbnailCornersIcons/Rounded'
import { MosaicIcon } from '@src/pages/Widget/Edit/Icons/LayoutIcons/Mosaic'
import { WallIcon } from '@src/pages/Widget/Edit/Icons/LayoutIcons/Wall'
import { SquareIcon as ShapeSquareIcon } from '@src/pages/Widget/Edit/Icons/ThumbnailShapeIcons/Square'
import { PortraitIcon } from '@src/pages/Widget/Edit/Icons/ThumbnailShapeIcons/Portrait'
import { StoriesIcon } from '@src/pages/Widget/Edit/Icons/ThumbnailShapeIcons/Stories'
import logo from '../../../assets/img/site/logo_alone_white.png'
import { Modal } from '@src/components/Modal'
import { ADALONG_COLORS } from '@src/styles'


const ASPECT_RATIOS = {
  square: 100,
  portrait: 125,
  stories: 177.78,
} as const

const DEFAULT_CONFIG = {
  MEDIA_PER_ROW: 3,
  IMAGE_SIZE: 194,
  GUTTER: 6,
  MAX_WIDTH: 600,
  HARMONIZE: true,
  MOSAIC: false,
  ROUNDED: false,
  ASPECT_RATIO: 'square' as keyof typeof ASPECT_RATIOS
}

type Media = {
  id: string
  image: string
  products?: { link: string }[]
}

interface EmbedHTMLProps {
  widgetId?: string
}

const EmbedHTML: FC<EmbedHTMLProps> = ({ widgetId = '' }) => {
  const [disableLinks, setDisableLinks] = useState(false)
  const [showLocalizationModal, setShowLocalizationModal] = useState(false)
  const [widgetIdInput, setWidgetIdInput] = useState(widgetId)
  const [widget, setWidget] = useState<any>(null)
  const [medias, setMedias] = useState<Media[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [exportHtml, setExportHtml] = useState('')
  const [copiedType, setCopiedType] = useState<string | null>(null)
  
  // Configuration options
  const [maxMedia, setMaxMedia] = useState<number | undefined>(undefined)
  const [mediaPerRow, setMediaPerRow] = useState<number>(DEFAULT_CONFIG.MEDIA_PER_ROW)
  const [localization, setLocalization] = useState('')
  const [mosaic, setMosaic] = useState(DEFAULT_CONFIG.MOSAIC)
  const [rounded, setRounded] = useState(DEFAULT_CONFIG.ROUNDED)
  const [gutter, setGutter] = useState<number>(DEFAULT_CONFIG.GUTTER)
  const [maxWidth, setMaxWidth] = useState<number>(DEFAULT_CONFIG.MAX_WIDTH)
  const [aspectRatio, setAspectRatio] = useState<keyof typeof ASPECT_RATIOS>(DEFAULT_CONFIG.ASPECT_RATIO)
  
  useEffect(() => {
    document.title = "Embed AdAlong UGC Gallery"; 
  }, []);

  // Reset exportHtml when any display option changes
  useEffect(() => {
    setExportHtml('')
  }, [maxMedia, mediaPerRow, localization, mosaic, rounded, gutter, maxWidth, aspectRatio])

  // Helper function to check if width exceeds newsletter recommendation
  const checkWidthExceedsRecommended = () => {
    return maxWidth > 600
  }

  // Localization dropdown state
  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false)
  const [availableLocalizations, setAvailableLocalizations] = useState<any[]>([])

  // Data fetching for localizations
  const { data: userConfig } = useQueryUserConfig()
  const { data: productCountryMappings } = useQueryProductsAvailableCountries()

  // Set up available localizations based on user's countries
  useEffect(() => {
    if (!productCountryMappings || !userConfig?.user?.catalogCountries?.length) return

    const userCountries = userConfig.user.catalogCountries
    const filtered = productCountryMappings.res.filter((mapping: any) =>
      userCountries.includes(mapping.isoCode)
    )
    setAvailableLocalizations(filtered)
  }, [productCountryMappings, userConfig])

  // Auto-load widget data when component mounts or widgetId changes
  useEffect(() => {
    if (widgetId) {
      setWidgetIdInput(widgetId)
      handleFetch()
    }
  }, [widgetId, localization])

  // Helper function to check if current settings exceed max width
  const checkWidthExceedsMax = () => {
    const columnsCount = mosaic ? 3 : mediaPerRow
    const totalGap = mosaic ? gutter * 4 : gutter * columnsCount
    const calculatedImageSize = Math.floor((maxWidth - totalGap) / columnsCount)
    return calculatedImageSize < 50 // Images would be too small
  }

  const getCalculatedImageSize = () => {
    const columnsCount = mosaic ? 3 : mediaPerRow
    const totalGap = mosaic ? gutter * 4 : gutter * columnsCount
    return Math.floor((maxWidth - totalGap) / columnsCount)
  }

  const handleFetch = async () => {
    const id = widgetId || widgetIdInput
    if (!id.trim()) {
      setError('No widget ID provided')
      return
    }
    try {
      setLoading(true)
      setError('')
      
      const widgetData = await getWidget(id)
      setWidget(widgetData)
      
      const apiKey = widgetData.token
      const settings = await getWidgetSettings(apiKey)
      
      if (localization) {
        settings.localization = localization
      }
      
      const contentResponse = await getWidgetContent(apiKey, settings)
      const content = contentResponse.res?.medias || contentResponse.medias || contentResponse
      
      // Filter out videos and Instagram content (if needed)
       const filtered = Array.isArray(content) ? content.filter(
        (media: any) =>
          media.type !== 'video' &&
          !media.image?.includes('cdninstagram.com')
      ) : []
      
      setMedias(filtered)
    } catch (e: any) {
      setError(e.message || 'Failed to load widget data')
      setMedias([])
      setWidget(null)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = (forceDisableLinks = false) => {
    const linksDisabled = forceDisableLinks || disableLinks
    if (!localization && availableLocalizations.length > 0 && !linksDisabled) {
      setShowLocalizationModal(true)
      return
    }
    setDisableLinks(linksDisabled)
    const html = generateExportableHtml(
      medias,
      { mediaPerRow, mosaic, rounded, gutter, maxWidth, maxMedia, aspectRatio, localization, availableLocalizations, disableLinks: linksDisabled }
    )
    setExportHtml(html)
  }

  const handleCopyHtml = async () => {
    if (!exportHtml) return
    try {
      await navigator.clipboard.writeText(exportHtml)
      setCopiedType('html')
      setTimeout(() => setCopiedType(null), 2000)
    } catch (err) {
      console.error('Failed to copy HTML:', err)
    }
  }

  const renderHtmlPreview = () => {
    if (medias.length === 0) return null

    const html = generateExportableHtml(
      medias,
      { mediaPerRow, mosaic, rounded, gutter, maxWidth, maxMedia, aspectRatio, localization, availableLocalizations }
    )
  
    return (
      <div className={previewContainerClass}>
        <div className={sectionTitleClass}>
          Preview
        </div>
        <div className={previewFrameClass}>
          <div
            className={previewContentClass}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={mainWrapperClass}>
      <PerfectScrollbar className={scrollableContentClass}>
        <div className={logoContainerClass}>
          <img src={logo} alt="AdAlong Logo" className={logoClass} />
        </div>
        
        <div className={titleClass}>
          Embed your UGC gallery
        </div>

        {loading ? (
          <div className={formContainerClass}>
            <div className={sectionTitleClass}>
              Loading Widget Data...
            </div>
          </div>
        ) : null}

        {error ? (
          <div className={formContainerClass}>
            <div className={alertErrorClass}>
              {error}
            </div>
          </div>
        ) : null}

        {widget && !loading ? (
          <div className={formContainerClass}>
            <div className={sectionTitleClass}>
              Widget: {widget.name}
            </div>
            <div className={alertSuccessClass}>
              Loaded: {medias.length} UGCs
            </div>
          </div>
        ) : null}

        {medias.length > 0 ? (
          <div className={formContainerClass}>
            <div className={sectionTitleClass}>
              Display Options
            </div>
            
            <div className={optionsGridClass}>
              {/* First Row */}
              <div className={optionCardClass}>
                <div className={cardTitleClass}>UGC per row</div>
                <input
                  className={numberInputClass}
                  type="number"
                  value={mediaPerRow}
                  onChange={(e) => setMediaPerRow(e.target.value ? parseInt(e.target.value) : DEFAULT_CONFIG.MEDIA_PER_ROW)}
                  min={1}
                  max={6}
                  disabled={mosaic}
                />
              </div>

              <div className={optionCardClass}>
                <div className={cardTitleClass}>Max UGC</div>
                <input
                  className={numberInputClass}
                  type="number"
                  value={maxMedia ?? ''}
                  onChange={(e) => setMaxMedia(e.target.value ? parseInt(e.target.value) : undefined)}
                  min={1}
                  placeholder="All"
                />
                <div className={optionalTextClass}>(optional)</div>
              </div>

              <div className={optionCardClass} id="localization-card">
                <div className={cardTitleClass}>Localization</div>
                {availableLocalizations.length > 0 ? (
                  <div className={localizationDropdownWrapperClass}>
                    <DropDown
                      title={localization || 'Select a localization'}
                      display={displayDropdown}
                      onDisplay={setDisplayDropdown}
                    >
                      {availableLocalizations.map((mapping, i) =>
                        mapping.name !== localization ? (
                          <div
                            className={localizationOptionClass}
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation()
                              setLocalization(mapping.name)
                              setDisableLinks(false)
                              setDisplayDropdown(false)
                            }}
                          >
                            {mapping.name}
                          </div>
                        ) : null
                      )}
                      {localization ? (
                        <div
                          className={localizationOptionClass}
                          onClick={(e) => {
                            e.stopPropagation()
                            setLocalization('')
                            setDisplayDropdown(false)
                          }}
                        >
                          Clear selection
                        </div>
                      ) : null}
                    </DropDown>
                  </div>
                ) : (
                  <div className={optionalTextClass}>No localizations available</div>
                )}
                <div className={optionalTextClass}>(optional)</div>
              </div>

              {/* Second Row */}
              <div className={optionCardClass}>
                <div className={cardTitleClass}>Corners</div>
                <div className={selectorRowClass}>
                  <div className={selectorItemClass}>
                    <SquareIcon 
                      onClick={() => setRounded(false)} 
                      selected={!rounded} 
                    />
                    <span className={selectorLabelClass}>SHARP</span>
                  </div>
                  <div className={selectorItemClass}>
                    <RoundedIcon 
                      onClick={() => setRounded(true)} 
                      selected={rounded} 
                    />
                    <span className={selectorLabelClass}>ROUNDED</span>
                  </div>
                </div>
              </div>

              <div className={optionCardClass}>
                <div className={cardTitleClass}>Layout</div>
                <div className={selectorRowClass}>
                  <div className={selectorItemClass}>
                    <WallIcon 
                      onClick={() => setMosaic(false)} 
                      selected={!mosaic} 
                    />
                    <span className={selectorLabelClass}>WALL</span>
                  </div>
                  <div className={selectorItemClass}>
                    <MosaicIcon 
                      onClick={() => setMosaic(true)} 
                      selected={mosaic} 
                    />
                    <span className={selectorLabelClass}>MOSAIC</span>
                  </div>
                </div>
              </div>


              {/* Third Row - Aspect Ratio icons and size options */}
              <div className={optionCardClass}>
                <div className={cardTitleClass}>Aspect Ratio</div>
                <div className={selectorRowClass}>
                  <div className={selectorItemClass}>
                    <ShapeSquareIcon
                      onClick={() => setAspectRatio('square')}
                      selected={aspectRatio === 'square'}
                    />
                    <span className={selectorLabelClass}>SQUARE</span>
                  </div>
                  <div className={selectorItemClass}>
                    <PortraitIcon
                      onClick={() => setAspectRatio('portrait')}
                      selected={aspectRatio === 'portrait'}
                    />
                    <span className={selectorLabelClass}>PORTRAIT</span>
                  </div>
                  <div className={selectorItemClass}>
                    <StoriesIcon
                      onClick={() => setAspectRatio('stories')}
                      selected={aspectRatio === 'stories'}
                    />
                    <span className={selectorLabelClass}>STORIES</span>
                  </div>
                </div>
              </div>

              <div className={optionCardClass}>
                <div className={cardTitleClass}>Spacing (px)</div>
                <input
                  className={numberInputClass}
                  type="number"
                  value={gutter}
                  onChange={(e) => setGutter(parseInt(e.target.value) || DEFAULT_CONFIG.GUTTER)}
                  min={0}
                  max={50}
                />
              </div>

              {/* Fourth Row - Max Width with warning when exceeds recommendation */}
              <div className={checkWidthExceedsRecommended() ? warningCardClass : optionCardClass}>
                <div className={cardTitleClass}>Width (px)</div>
                <input
                  className={numberInputClass}
                  type="number"
                  value={maxWidth}
                  onChange={(e) => setMaxWidth(parseInt(e.target.value) || DEFAULT_CONFIG.MAX_WIDTH)}
                  min={200}
                  max={1200}
                />
                {checkWidthExceedsRecommended() ? (
                  <div className={warningTextClass}>
                    ⚠️ The recommended width for newsletters is 600px
                  </div>
                ) : null}
                {checkWidthExceedsMax() ? (
                  <div className={warningTextClass}>
                    ⚠️ Images would be too small ({getCalculatedImageSize()}px each)
                  </div>
                ) : (
                  <div className={optionalTextClass}>
                    Image size: {getCalculatedImageSize()}px each
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null }

        {renderHtmlPreview()}

        {medias.length > 0 ? (
          <div className={exportFormContainerClass}>
            <div className={sectionTitleClass}>
              Export HTML
            </div>
            <div className={exportButtonContainerClass}>
              <ActionButton variant="blue" onClick={() => handleExport()}>
                Generate HTML Code
              </ActionButton>
            </div>

            {exportHtml ? (
              <FormLine title="HTML Code">
                <TextArea
                  value={exportHtml}
                  readOnly
                  rows={10}
                  className={codeOutputTextAreaClass}
                />
                <div className={copyButtonContainerClass}>
                  <ActionButton 
                    variant="transparent" 
                    onClick={handleCopyHtml}
                    disabled={!exportHtml}
                  >
                    {copiedType === 'html' ? 'Copied!' : 'Copy HTML'}
                  </ActionButton>
                </div>
              </FormLine>
            ) : null}
          </div>
        ) : null}

        {showLocalizationModal ? (
          <Modal onClose={() => setShowLocalizationModal(false)}>
            <div className={modalContentClass}>
              <h2 className={modalTitleClass}>Localization missing</h2>
              <p className={modalTextClass}>
                 You did not select a localization before generating HTML. Localization ensures product links are correct for your region.
              </p>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 8 }}>
                <ActionButton variant="blue" onClick={() => {
                  setShowLocalizationModal(false)
                }}>
                  Select localization
                </ActionButton>
                <ActionButton variant="gray" onClick={() => {
                  setShowLocalizationModal(false)
                  setTimeout(() => handleExport(true), 0)
                }}>
                  I don't want links
                </ActionButton>
              </div>
            </div>
          </Modal>
        ) : null}

      </PerfectScrollbar>
    </div>
  )
}

function generateExportableHtml(
  medias: Media[],
  options: {
    mediaPerRow?: number
    mosaic?: boolean
    rounded?: boolean
    gutter?: number
    maxWidth?: number
    maxMedia?: number
    aspectRatio?: keyof typeof ASPECT_RATIOS, 
    localization?: string
    availableLocalizations?: string[]
    disableLinks?: boolean
  } = {}
): string {
  const { mediaPerRow = DEFAULT_CONFIG.MEDIA_PER_ROW, mosaic = DEFAULT_CONFIG.MOSAIC, rounded = DEFAULT_CONFIG.ROUNDED, gutter = DEFAULT_CONFIG.GUTTER, maxWidth = DEFAULT_CONFIG.MAX_WIDTH, maxMedia, aspectRatio = DEFAULT_CONFIG.ASPECT_RATIO, localization, availableLocalizations = [], disableLinks = false } = options
  const effectiveMaxMedia = maxMedia ?? medias.length
  const slice = medias.slice(0, effectiveMaxMedia)

  const columnsCount = mosaic ? 3 : mediaPerRow
  const totalGap = mosaic ? gutter * 4 : gutter * columnsCount
  const imageSize = Math.floor((maxWidth - totalGap) / columnsCount)

  let html = `
<table border="0" cellpadding="0" cellspacing="0" width="${maxWidth}" align="center" style="border-collapse:separate;">
  <tbody>
    ${mosaic
      ? renderMosaic(slice, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)
      : renderStandard(slice, mediaPerRow, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)}
  </tbody>
</table>`

  return html.trim()
}

function renderStandard(
  medias: Media[],
  mediaPerRow: number,
  rounded?: boolean,
  imageSize: number = DEFAULT_CONFIG.IMAGE_SIZE,
  gutter: number = DEFAULT_CONFIG.GUTTER,
  aspectRatio: keyof typeof ASPECT_RATIOS = DEFAULT_CONFIG.ASPECT_RATIO,
  localization?: string,
  availableLocalizations?: string[],
  disableLinks?: boolean,
): string {
  const chunked = chunkArray(medias, mediaPerRow)
  let html = ''
  chunked.forEach((row) => {
    html += '<tr>'
    row.forEach((media) => {
      html += renderCell(media, true, mediaPerRow, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)
    })
    html += '</tr>'
  })
  return html
}

function renderMosaic(
  medias: Media[],
  rounded?: boolean,
  imageSize: number = DEFAULT_CONFIG.IMAGE_SIZE,
  gutter: number = DEFAULT_CONFIG.GUTTER,
  aspectRatio: keyof typeof ASPECT_RATIOS = DEFAULT_CONFIG.ASPECT_RATIO,
  localization?: string,
  availableLocalizations?: string[],
  disableLinks?: boolean,
): string {
  if (medias.length <= 5) {
    // Single mosaic block for 5 or fewer images
    let html = '<tr>'
    for (let i = 0; i < 2 && i < medias.length; i++) {
      html += renderCell(medias[i], true, 4, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)
    }
    if (medias[4]) {
      html += `<td rowspan="2" style="
        width:${imageSize * 2}px;
        height:${imageSize * 2 + gutter * 2}px;
        padding:${gutter / 2}px;
        line-height:0;
        font-size:0;
        vertical-align:top;
      ">`
      html += `
        <div style="display:block; line-height:0; font-size:0;">
          ${renderAnchor(medias[4], imageSize * 2 + gutter, true, rounded, aspectRatio, localization, availableLocalizations, disableLinks)}
        </div>
      `
      html += '</td>'
    }  
    html += '</tr><tr>'
    for (let i = 2; i < 4 && i < medias.length; i++) {
      html += renderCell(medias[i], true, 4, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)
    }
    html += '</tr>'
    return html
  } else {
    // Multiple mosaic blocks or grid layout for more than 5 images
    let html = ''
    let processedCount = 0
    
    // Create mosaic blocks of 5 images each
    while (processedCount < medias.length) {
      const remainingCount = medias.length - processedCount
      
      if (remainingCount >= 5) {
        // Full mosaic block
        const mosaicSlice = medias.slice(processedCount, processedCount + 5)
        html += '<tr>'
        for (let i = 0; i < 2; i++) {
          html += renderCell(mosaicSlice[i], true, 4, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations)
        }
        html += `<td rowspan="2" style="
          width:${imageSize * 2}px;
          height:${imageSize * 2 + gutter * 2}px;
          padding:${gutter / 2}px;
          line-height:0;
          font-size:0;
          vertical-align:top;
        ">`
        html += `
          <div style="display:block; line-height:0; font-size:0;">
            ${renderAnchor(mosaicSlice[4], imageSize * 2 + gutter, true, rounded, aspectRatio, localization, availableLocalizations)}
          </div>
        `
        html += '</td>'
        html += '</tr><tr>'
        for (let i = 2; i < 4; i++) {
          html += renderCell(mosaicSlice[i], true, 4, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations)
        }
        html += '</tr>'
        processedCount += 5
      } else {
        // Remaining images in a standard grid
        const remainingSlice = medias.slice(processedCount)
        const chunked = chunkArray(remainingSlice, 3)
        chunked.forEach((row) => {
          html += '<tr>'
          row.forEach((media) => {
          html += renderCell(media, true, 3, rounded, imageSize, gutter, aspectRatio, localization, availableLocalizations, disableLinks)
          })
          html += '</tr>'
        })
        break
      }
    }
    return html
  }
}

function renderCell(
  media: Media,
  harmonize: boolean | undefined,
  perRow: number,
  rounded: boolean | undefined,
  imageSize: number = DEFAULT_CONFIG.IMAGE_SIZE,
  gutter: number,
  aspectRatio: keyof typeof ASPECT_RATIOS = DEFAULT_CONFIG.ASPECT_RATIO, 
  localization?: string,
  availableLocalizations?: string[],
  disableLinks?: boolean,
): string {
  const width = harmonize ? `${imageSize}px` : `${(100 / perRow).toFixed(2)}%`
  const pixelSize = imageSize
  return `
  <td style="width:${width}; vertical-align:top; padding:${gutter / 2}px;">
    ${renderAnchor(media, pixelSize, harmonize, rounded, aspectRatio, localization, availableLocalizations, disableLinks)}
  </td>`
}

function renderAnchor(
  media: Media,
  size?: number,
  harmonize?: boolean,
  rounded?: boolean,
  aspectRatio: keyof typeof ASPECT_RATIOS = DEFAULT_CONFIG.ASPECT_RATIO,
  localization?: string,
  availableLocalizations?: string[],
  disableLinks?: boolean,
): string {
  const link = media.products?.[0]?.link || '#'
  const borderRadius = rounded ? 'border-radius:8px;' : ''

  let imgAttributes = ''
  let imgStyles = `display:block;${borderRadius}`

  if (harmonize && size) {
    // Calculate height based on aspect ratio
    const ratio = ASPECT_RATIOS[aspectRatio] / 100
    const height = Math.round(size * ratio)
    imgAttributes = `width="${size}" height="${height}"`
    imgStyles += `width:${size}px;height:${height}px;object-fit:cover;margin:0;border:0;`
  } else {
    imgStyles += 'width:100%;height:auto;'
  }

  // For clients with available localizations, only show <a> if links are enabled and a localization is selected
  // For clients without available localizations, always show <a>
  const hasLocalizations = availableLocalizations && availableLocalizations.length > 0;
  if (hasLocalizations) {
    if (disableLinks || !localization) {
      return `
        <!--[if mso]>
          <img src="${media.image}" ${imgAttributes} style="display:block;${borderRadius}" />
        <![endif]-->
        <!--[if !mso]><!-- -->
          <img src="${media.image}" ${imgAttributes} style="${imgStyles}" />
        <!--<![endif]-->
      `;
    }
  }
  return `
    <a href="${link}" target="_blank">
      <!--[if mso]>
        <img src="${media.image}" ${imgAttributes} style="display:block;${borderRadius}" />
      <![endif]-->
      <!--[if !mso]><!-- -->
        <img src="${media.image}" ${imgAttributes} style="${imgStyles}" />
      <!--<![endif]-->
    </a>`
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

const mainWrapperClass = style({
  backgroundColor: '#000',
  color: '#fff',
  fontFamily: "'Public Sans', sans-serif",
  height: '100vh',
  overflow: 'hidden',
  position: 'relative'
})

const scrollableContentClass = style({
  height: '100%',
  overflow: 'hidden',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})

const formContainerClass = style({
  backgroundColor: '#111',
  borderRadius: '10px',
  padding: '30px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  marginBottom: '20px',
  width: '60%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})

const titleClass = style({
  margin: '0 0 25px 0',
  textAlign: 'center',
  fontSize: '24px',
  fontWeight: 500,
  color: '#fff'
})

const logoContainerClass = style({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '20px'
})

const logoClass = style({
  height: '60px',
  width: 'auto'
})

const sectionTitleClass = style({
  fontSize: '18px',
  fontWeight: 500,
  marginBottom: '15px',
  color: '#fff',
  textAlign: 'center'
})

const checkboxLabelClass = style({
  color: '#fff',
  fontFamily: "'Public Sans', sans-serif",
  fontSize: '14px',
  cursor: 'pointer'
})

const optionalTextClass = style({
  color: '#888',
  fontSize: '12px',
  fontWeight: 400
})

const localizationOptionClass = style({
  padding: '8px 12px',
  cursor: 'pointer',
  color: '#fff',
  $nest: {
    '&:hover': {
      backgroundColor: '#333'
    }
  }
})

const numberInputClass = style({
  backgroundColor: '#222',
  border: '2px solid #444',
  borderRadius: '6px',
  color: '#fff',
  fontFamily: "'Public Sans', sans-serif",
  fontSize: '16px',
  padding: '10px 12px',
  width: '120px',
  // Restore default browser appearance for number input (shows arrows)
  $nest: {
    '&:focus': {
      borderColor: '#021ed3',
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(2, 30, 211, 0.2)'
    },
    '&:hover': {
      borderColor: '#555'
    },
    '&:disabled': {
      backgroundColor: '#333',
      color: '#666',
      cursor: 'not-allowed',
      borderColor: '#333'
    }
  }
})

const alertSuccessClass = style({
  backgroundColor: 'rgba(34, 197, 94, 0.1)',
  border: '1px solid rgba(34, 197, 94, 0.3)',
  color: '#22c55e',
  borderRadius: '4px',
  padding: '12px',
  fontSize: '14px'
})

const alertErrorClass = style({
  backgroundColor: 'rgba(255, 60, 60, 0.1)',
  border: '1px solid rgba(255, 60, 60, 0.3)',
  color: '#fff',
  borderRadius: '4px',
  padding: '12px',
  fontSize: '14px'
})

const previewContainerClass = style({
  backgroundColor: '#222',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '20px',
  border: '1px solid #333'
})

const previewFrameClass = style({
  backgroundColor: '#333',
  borderRadius: '4px',
  padding: '16px',
  overflowX: 'auto',
  minHeight: '200px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  border: '1px solid #444'
})

const previewContentClass = style({
  width: '100%',
  display: 'flex',
  justifyContent: 'center'
})

const localizationDropdownWrapperClass = style({
  minWidth: 180,
  display: 'inline-block',
})

const codeOutputTextAreaClass = style({
  backgroundColor: '#222 !important',
  border: '1px solid #333 !important',
  color: '#fff !important',
  fontFamily: 'monospace !important',
  fontSize: '0.875rem !important',
  width: '650px'
})

const exportFormContainerClass = style({
  backgroundColor: '#111',
  borderRadius: '10px',
  padding: '30px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  marginBottom: '20px',
  marginTop: '40px',
  width: '60%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})

const exportButtonContainerClass = style({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '20px'
})

const copyButtonContainerClass = style({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '10px'
})

const selectorRowClass = style({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '15px'
})

const selectorItemClass = style({
  fontSize: '0.7em',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  $nest: {
    '& svg': {
      width: '60px !important',
      height: '60px !important'
    }
  }
})

const selectorLabelClass = style({
  color: '#fff',
  fontFamily: "'Public Sans', sans-serif",
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.5px'
})

const optionsGridClass = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '20px',
  width: '100%',
  maxWidth: '800px',
  $nest: {
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '15px'
    },
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
      gap: '15px'
    }
  }
})

const optionCardClass = style({
  backgroundColor: '#222',
  borderRadius: '8px',
  padding: '20px',
  border: '1px solid #333',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  minHeight: '120px',
  justifyContent: 'center',
  $nest: {
    '&:hover': {
      borderColor: '#444',
      backgroundColor: '#252525'
    }
  }
})

const cardTitleClass = style({
  fontSize: '14px',
  fontWeight: 500,
  color: '#fff',
  textAlign: 'center',
  marginBottom: '8px'
})

const checkboxCenteredClass = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
})

const warningCardClass = style({
  backgroundColor: '#332200',
  borderRadius: '8px',
  padding: '20px',
  border: '2px solid #ff9500',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  minHeight: '120px',
  justifyContent: 'center',
  $nest: {
    '&:hover': {
      borderColor: '#ffaa00',
      backgroundColor: '#443300'
    }
  }
})

const warningTextClass = style({
  color: '#ff9500',
  fontSize: '11px',
  textAlign: 'center',
  fontWeight: 500,
  lineHeight: '1.3',
  marginTop: '4px'
})

const modalContentClass = style({
  background: ADALONG_COLORS.SMOOTH_BLACK,
  color: '#fff',
  padding: '32px 48px',
  borderRadius: 12,
  maxWidth: 520,
  margin: 'auto',
  textAlign: 'center',
  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const modalTitleClass = style({
  marginBottom: 16,
  fontSize: '1.5rem',
  fontWeight: 600,
})

const modalTextClass = style({
  marginBottom: 24,
  fontSize: '1rem',
})

export default EmbedHTML
